import React from 'react';
import { Plus, Minus, Type } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFontSize } from '@/context/FontSizeContext';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface FontSizeControlProps {
  compact?: boolean;
  showLabel?: boolean;
  vertical?: boolean;
}

export const FontSizeControl: React.FC<FontSizeControlProps> = ({
  compact = false,
  showLabel = false,
  vertical = false
}) => {
  const { fontSize, increaseFontSize, decreaseFontSize, resetFontSize } = useFontSize();

  const fontSizeLabels = {
    'small': 'Petit',
    'normal': 'Normal', 
    'large': 'Grand',
    'extra-large': 'Très grand'
  };

  const canDecrease = fontSize !== 'small';
  const canIncrease = fontSize !== 'extra-large';

  if (compact) {
    return (
      <div className={`flex ${vertical ? 'flex-col' : 'flex-row'} items-center gap-1`}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={decreaseFontSize}
              disabled={!canDecrease}
              aria-label="Diminuer la taille de police"
              className="h-8 w-8 p-0"
            >
              <Minus className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Diminuer la taille de police</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFontSize}
              aria-label="Taille de police normale"
              className="h-8 w-8 p-0"
            >
              <Type className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Taille normale ({fontSizeLabels[fontSize]})</p>
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={increaseFontSize}
              disabled={!canIncrease}
              aria-label="Augmenter la taille de police"
              className="h-8 w-8 p-0"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Augmenter la taille de police</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className={`flex ${vertical ? 'flex-col' : 'flex-row'} items-center gap-2`}>
      <Type className="h-4 w-4" />
      
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={decreaseFontSize}
          disabled={!canDecrease}
          aria-label="Diminuer la taille de police"
        >
          <Minus className="h-4 w-4" />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          onClick={resetFontSize}
          className="min-w-[4rem] text-xs"
        >
          {fontSizeLabels[fontSize]}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={increaseFontSize}
          disabled={!canIncrease}
          aria-label="Augmenter la taille de police"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      {showLabel && (
        <span className="text-sm font-medium">
          Taille de police
        </span>
      )}
    </div>
  );
};